import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useCardsInfiniteQuery,
    useDeleteCardsMutation,
} from './useCardsQuery';
import {useCardsSearch} from './useCardsSearch';
import {useCardsTableColumns} from './useCardsTableColumns';
import {CardsFilters} from './useCardsFilter';
import {Card} from '../types';

/**
 * Default sort: NEWEST FIRST, on `created`.
 *
 * The key must be immutable and non-null. `created` is set when the card is
 * attached and never moves, so it satisfies both — unlike `updated`, which
 * would reorder the list under an operator who is reading it.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Card> = {
    columnAccessor: 'created',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against. `id` is included because a Stripe
 * card id (`card_...`) is what an operator pastes in from the Stripe dashboard
 * when chasing a specific instrument; `last4` because it is the only part of
 * the number anyone can quote from a customer phone call.
 */
const matchesKeyword = (item: Card, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.name,
        item.brand,
        item.last4,
        item.country,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (item: Card, filters: CardsFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof Card];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listCards(filter, limit, nextToken)` takes a filter that carries the
 * customerId scope and no sort argument, so the server can neither narrow by
 * keyword nor order the result set. Everything below therefore operates on the
 * loaded window only. Sorting page 1 does not sort the dataset, and a keyword
 * matches only rows the browser has already received — the other rows were
 * never examined.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 *
 * In practice a customer holds a handful of cards, so the loaded window is
 * usually the whole set — but "usually" is not a guarantee the UI may state.
 */
export const useCards = (customerId: string, filters: CardsFilters = {}) => {
    const query = useCardsInfiniteQuery(customerId);
    const {columns} = useCardsTableColumns();
    const {keyword} = useCardsSearch();
    const deleteMutation = useDeleteCardsMutation(customerId);

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: Card[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise the expiry into ONE sortable number (YYYYMM) so the column
        // can display "9/2026" and sort chronologically. Sorting the displayed
        // string would order 10/2026 before 9/2026 — lexicographic order on a
        // value a user reads as a date.
        .map(item => ({
            ...item,
            expiresAtSortKey: (item.expYear && item.expMonth)
                ? item.expYear * 100 + item.expMonth
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<Card>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched`
    // is rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
